import { LOAD_TIMEOUT } from "common/constants";
import { ApplicationTimeoutError } from "common/errors/ApplicationTimeoutError";
import { CheckoutTokenData, CheckoutUpdateParameters } from "common/Messages";
import { timeout } from "common/timeout";
import { InlineSplitElement } from "common/types";
import { pick, uniqueId } from "lodash";
import { DecoratedFormState } from "redux-form";
import { CheckoutInfoItem } from "univapay-node";

import { FormDataCard } from "./components/flows/card";
import { PatchedCheckoutInfo } from "./redux/models/configuration";
import { Dispatch, StateShape, store } from "./redux/store";
import { InlineCallbackError } from "./utils/errors";

enum SplitCheckoutMessage {
    /**
     * Event triggered on apparition of a new iFrame
     */
    CONNECT = "connect",

    /**
     * Event triggered on formik value change
     */
    UPDATE = "update",

    SETUP = "setup",

    /**
     * Event triggered after connection when another form already exists to get the checkout information
     */
    SETUP_RESPONSE = "setup-response",
}

type EventConnectorProperties = {
    /**
     * Generated internal split checkout connector ID
     */
    senderId: string;

    /**
     * Merchant-registered checkout split id shared by the channel
     */
    splitId: string;

    /**
     * Merchant-registered sender checkout displayed elements
     */
    elements: InlineSplitElement[];
};

type SplitCheckoutEvent<Data> = EventConnectorProperties & {
    type: SplitCheckoutMessage;
    data?: Data;
};

type CheckoutInfoMessage = {
    checkoutInfo: CheckoutInfoItem;
};

type PromiseHandler = (...params: unknown[]) => void;

export const mapInlineElementToFieldName = (element: InlineSplitElement): string => {
    switch (element) {
        case InlineSplitElement.NAME:
            return "name";

        case InlineSplitElement.NAME_KANA:
            return "nameKana";

        case InlineSplitElement.DATA_CARD_INSTALLMENTS:
            return "installmentCycles";

        case InlineSplitElement.DATA_ACCEPT_RECURRING:
        case InlineSplitElement.DATA_ACCEPT_RECURRING_OPTIONAL:
            return "data.accept";

        case InlineSplitElement.DATA_CARDHOLDER_NAME:
            return "data.cardholder";

        case InlineSplitElement.DATA_CARD_NUMBER:
            return "data.cardNumber";

        case InlineSplitElement.BILLING_ADDRESS_CITY:
            return "data.city";

        case InlineSplitElement.BILLING_ADDRESS_COUNTRY:
            return "data.country";

        case InlineSplitElement.DATA_CVV:
            return "data.cvv";

        case InlineSplitElement.DATA_EXPIRY:
            return "data.exp";

        case InlineSplitElement.BILLING_ADDRESS_ADDRESS:
            return "data.line1";

        case InlineSplitElement.BILLING_ADDRESS_ADDRESS_COMPLEMENT:
            return "data.line2";

        case InlineSplitElement.PHONE_NUMBER:
            return "data.phoneNumber";

        case InlineSplitElement.BILLING_ADDRESS_STATE:
            return "data.state";

        case InlineSplitElement.BILLING_ADDRESS_ZIP:
            return "data.zip";

        default:
            return element;
    }
};

/**
 * Formik context will take a bit more time to load. Ensure it is present when connecting to ensure sent values consistency
 */
const waitForFormikContext = async () => waitForParameter(() => getCurrentForm());

const waitForParameter = async (getter: () => unknown) => {
    const INTERVAL_WAIT = 50;

    let interval;
    let timer = 0;

    return new Promise((resolve, reject) => {
        interval = setInterval(() => {
            timer = timer + INTERVAL_WAIT;
            if (getter()) {
                resolve(getter());
            }

            if (timer > LOAD_TIMEOUT) {
                reject();
            }
        }, INTERVAL_WAIT);
    }).finally(() => {
        clearInterval(interval);
    });
};

const updateInternalFormValues = async (message: SplitCheckoutEvent<CheckoutUpdateParameters>) => {
    await (store.dispatch as Dispatch).application.clientUpdateParameters({
        overrides: Object.keys(message.data).reduce((acc, key) => {
            acc[mapInlineElementToFieldName(key as InlineSplitElement)] = message.data[key];
            return acc;
        }, {}) as CheckoutTokenData,
        options: { forceUpdate: true },
    });
};

const getCurrentForm = () => {
    const state = store.getState() as StateShape;
    const activeForm = Object.keys(state.form)[0];
    if (!activeForm) {
        return;
    }

    return state.form[activeForm] as DecoratedFormState<FormDataCard, InlineCallbackError>;
};

export class SplitCheckoutConnector {
    id: string;
    channel?: BroadcastChannel;

    splitId: string;
    splitElements: InlineSplitElement[];
    private checkoutInfo: PatchedCheckoutInfo;

    private setupCallbacks: { resolve?: PromiseHandler; reject?: PromiseHandler } = {};

    constructor(splitId: string, splitElements: InlineSplitElement[]) {
        this.id = uniqueId();

        this.splitId = splitId;
        this.splitElements = splitElements;

        this.channel = new BroadcastChannel(`univapay-split-checkout-${splitId}`);
        this.channel.onmessage = this.handleMessage;
    }

    async connect() {
        await waitForFormikContext();
        this.sendMessage({
            type: SplitCheckoutMessage.CONNECT,
            data: pick(getCurrentForm().values, this.splitElements.map(mapInlineElementToFieldName)),
        });
    }

    private async setupIteration(iteration = 1) {
        const MAX_ITERATION = 2;

        const response = await Promise.race([
            new Promise((resolve, reject) => {
                this.setupCallbacks = { resolve, reject };
                this.sendMessage({ type: SplitCheckoutMessage.SETUP });
            }),
            timeout(2500, new ApplicationTimeoutError(2500)),
        ])
            .then((data: SplitCheckoutEvent<CheckoutInfoMessage>) => data)
            .catch(() => (iteration <= MAX_ITERATION ? this.setupIteration(iteration + 1) : undefined));

        this.setupCallbacks = {};

        return response;
    }

    async setup() {
        const response = await this.setupIteration();
        this.checkoutInfo = response?.data?.checkoutInfo;

        return this.checkoutInfo;
    }

    update(values = getCurrentForm().values) {
        return this.sendMessage({
            type: SplitCheckoutMessage.UPDATE,
            data: pick(values, this.splitElements.map(mapInlineElementToFieldName)),
        });
    }

    close() {
        this.channel.close();
    }

    async getCheckoutInfo() {
        if (this.checkoutInfo) {
            return this.checkoutInfo;
        }

        return this.setup();
    }

    setCheckoutInfo(checkoutInfo: PatchedCheckoutInfo) {
        this.checkoutInfo = checkoutInfo;
    }

    private handleMessage = async (event: MessageEvent) => {
        switch (event.data.type) {
            case SplitCheckoutMessage.CONNECT:
                await updateInternalFormValues(event.data);
                this.update(); // Ensures that other form share the initial values
                break;

            case SplitCheckoutMessage.SETUP:
                this.sendMessageWhen(() => (this.checkoutInfo ? { checkoutInfo: this.checkoutInfo } : undefined), {
                    type: SplitCheckoutMessage.SETUP_RESPONSE,
                });
                break;

            case SplitCheckoutMessage.SETUP_RESPONSE:
                this.setupCallbacks.resolve?.(event.data);
                break;

            case SplitCheckoutMessage.UPDATE:
                await updateInternalFormValues(event.data);
                break;

            default:
                console.warn("Unknown split checkout message received", event.data.type);
        }
    };

    private sendMessageWhen = async <Data>(
        iterator: () => Data,
        message: Omit<SplitCheckoutEvent<Data>, keyof EventConnectorProperties>,
        options?: { maxIterations?: number; interval?: number }
    ) => {
        const { maxIterations = -1, interval = 200 } = options || {};

        let iteration = 0;
        while (!iterator() && (maxIterations === -1 ? true : iteration <= maxIterations)) {
            await new Promise((resolve) => setTimeout(resolve, interval));
            iteration++;
        }

        if (iterator()) {
            this.sendMessage({ ...message, data: iterator() });
        }
    };

    private sendMessage<Data>(message: Omit<SplitCheckoutEvent<Data>, keyof EventConnectorProperties>): void {
        this.channel.postMessage({
            ...message,
            splitId: this.splitId,
            elements: this.splitElements,
            senderId: this.id,
        });
    }
}
